import type { ApiErrorCode } from "#types/core/errors.ts";
import type { HttpVerbs } from "../types.ts";

export class ApiResponseError extends Error {
  readonly httpStatusCode: number;
  readonly data?: unknown;
  /**
   * The requested method when this error occurred.
   */
  readonly requestedMethod?: HttpVerbs;
  /**
   * The requested url when this error occurred.
   */
  readonly requestedUrl?: string;
  /**
   * Response type
   */
  readonly type?: ResponseType;

  readonly correlationId?: string;

  constructor({
    httpStatusCode,
    requestedMethod,
    correlationId,
    requestedUrl,
    data,
    type,
  }: {
    httpStatusCode: number;
    requestedMethod?: HttpVerbs;
    correlationId?: string | null;
    requestedUrl?: string;
    type?: ResponseType;
    data?: unknown;
  }) {
    const msg = [
      `Requested resource: ${requestedMethod?.toUpperCase() || "unknown"} ${
        requestedUrl || "unknown"
      }.`,
      `Status code: ${httpStatusCode}`,
      `Response type: ${type || "unknown"}`,
      `Response body: ${JSON.stringify(data)}`,
      `Correlation id: ${correlationId ?? "missing"}`,
    ].join("\n");
    super(msg);
    this.name = "ApiResponseError";
    this.httpStatusCode = httpStatusCode;
    this.data = data;
    this.requestedUrl = requestedUrl;
    this.requestedMethod = requestedMethod;
    this.type = type;
    this.correlationId = correlationId ?? "missing";
  }

  static isServerError(error: any): error is ServerError {
    return Boolean(
      error &&
        error.name === "ApiResponseError" &&
        error.data &&
        typeof error.data.code === "string" &&
        typeof error.data.short_message === "string" &&
        typeof error.data.long_message === "string",
    );
  }

  static isSsnError = serverErrorCheck("40009");
  static isNotFoundError = serverErrorCheck("40400");
  static isAlreadyRegisteredError = serverErrorCheck("40004");
  static isEmailInUseError = serverErrorCheck("40106");
  static isPhoneNumberInUseError = serverErrorCheck("40107");
  static isPhoneNumberValidationError = serverErrorCheck("40005");
  static isEmailValidationError = serverErrorCheck("40007");
  static isOtpRequiredError = serverErrorCheck("20010");
  static isAccessDeniedError = serverErrorCheck("40101");
  static isTokenValidationError = serverErrorCheck("40000");
  static isInvalidOtpError = serverErrorCheck("40111", "40025");
  static isInvalidRegistrationCode = serverErrorCheck("40108");
  static isPasswordValidationError = serverErrorCheck("40006");
  static isUserDoesNotExistError = serverErrorCheck("40113");
  static isPhoneVerifiedRequiredError = serverErrorCheck("42203");
  static isUserLastInCompanyError = serverErrorCheck("40912");
  static isUserMandatePendingError = serverErrorCheck("40917");
  static isInvalidCampaignLinkError = serverErrorCheck("40038");
  static isUserDeactivatingError = serverErrorCheck("40902");
  static isPaymentInProgressError = serverErrorCheck("40903");
  static isShareInitiatedError = serverErrorCheck("40904");
  static isTooManyRequestsError = serverErrorCheck("42900");
  static isGuardiansRequiredError = serverErrorCheck("40317");
  static isUserUnderageError = serverErrorCheck("40318");
  static isEmailVerificationRequiredError = serverErrorCheck("40320");
  static isSufficientPermissionError = serverErrorCheck("40301");
  static isCampaignImageBase64Error = serverErrorCheck("40039");
  static isCampaignImagePngError = serverErrorCheck("40044");
  static isCampaignImageJpegError = serverErrorCheck("40045");
  static isUnavailableForLegalReasons = serverErrorCheck("20211");
  static isWaitingForCampaignStatistics = serverErrorCheck("20212");
  static isInvalidPayDateError = serverErrorCheck("40041");
  static isInvalidOcrError = serverErrorCheck("40044");
  static isOngoingPaymentError = serverErrorCheck("40916");
  static isPaymentAmountOutOfRangeError = serverErrorCheck("40042");
  static isContentTrashedError = serverErrorCheck("40908");
  static isServiceTermsError = serverErrorCheck("40920");
  static isPaymentServiceUnavailableError = serverErrorCheck("50302");
  static isInvalidFileError = serverErrorCheck("40019");
  static isMemberAlreadyExistsError = serverErrorCheck(
    "PERSONAS_ERROR__MEMBER_ALREADY_EXISTS",
  );
  static isSwishPayeeReferenceError = serverErrorCheck("42210");
  static isSwishInvalidOrMissingCurrencyError = serverErrorCheck("42211");
  static isSwishAmountTooLowError = serverErrorCheck("42212");
  static isSwishInvalidAmountError = serverErrorCheck("42213");
  static isSwishMissingMerchantError = serverErrorCheck("42214");
  static isSwishWrongFormattedMessageError = serverErrorCheck("42215");
  static isBankIdExpired = serverErrorCheck("40027");
  static isFormExpired = serverErrorCheck("40302");
  static isUploadLimitReachedError = serverErrorCheck("42217");
  static isCompanyTypeNotSupportedError = serverErrorCheck("40399");
  static isFirstSignerNotUserError = serverErrorCheck("40999");
  static isNotCompanySignatoryError = serverErrorCheck("40398");
  static isCreditsafeRejectedError = serverErrorCheck("40322");
}

function serverErrorCheck(...codes: ApiErrorCode[]) {
  return (error: any): error is ServerError => {
    return (
      ApiResponseError.isServerError(error) && codes.includes(error.data.code)
    );
  };
}

interface ServerError extends ApiResponseError {
  readonly data: {
    readonly code: ApiErrorCode;
    readonly short_message: string;
    readonly long_message: string;
    readonly keys?: string[];
  };
}
